import React from "react";
import type { ExpiryItem } from "../hooks/useExpiryItems";
import { getNextReminderDays } from "../utils/expiryUtils";
import { COLOR } from "../constants/expiry";
import { BellIcon } from "./Icons";

// Urgency thresholds for the reminder cell colour
const CRITICAL_DAYS = 3;
const WARNING_DAYS = 10;

interface NextReminderCellProps {
  item: ExpiryItem;
  /** Days-before-expiry thresholds from the user's reminder settings.
   *  Falls back to the built-in constant when not supplied. */
  reminderDays?: number[];
}

/**
 * Renders the "Next Reminder Mail" column cell.
 * Shows a bell icon + how many days until the next automated reminder fires,
 * calculated from the user's configured reminder schedule.
 * Returns "—" when the item is already expired or all reminders have been sent.
 */
const NextReminderCell: React.FC<NextReminderCellProps> = ({
  item,
  reminderDays,
}) => {
  const days = getNextReminderDays(item, reminderDays);

  if (days === null) {
    return <span className={`${COLOR.muted} text-xs`}>—</span>;
  }

  let colorClass: string = COLOR.subtle;
  if (days <= CRITICAL_DAYS) colorClass = COLOR.danger;
  else if (days <= WARNING_DAYS) colorClass = COLOR.warning;

  const label = days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days}d`;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${colorClass}`}
    >
      <BellIcon className="h-3.5 w-3.5 flex-shrink-0" />
      {label}
    </span>
  );
};

export default NextReminderCell;
